import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import { RequestBody, ResponseBody } from "@src/packages/openapi/helpers";
import { paths } from "@src/packages/openapi/schema";

type CareTeamCollection = paths["/patients/{patientId}/care-team"];
type CareTeamRecordEndpoint =
  paths["/patients/{patientId}/care-team/{recordId}"];

export type CareConnection = ResponseBody<
  CareTeamCollection["get"]
>["data"][number];
export type CareConnectionKind = CareConnection["kind"];
export type CareTeamRecordInput = RequestBody<CareTeamCollection["post"]>;
export type CareTeamRecordUpdateInput = RequestBody<
  CareTeamRecordEndpoint["patch"]
>;

const queryKey = (patientId: string | undefined) => ["care-team", patientId];

/**
 * Patient-owned care-team records, served by the dashboard care-team API.
 * Cycle association travels through `cycleIds`; identity matching, secure
 * sharing, and EHR connection are future backend concerns and are not part
 * of this contract.
 */
export function useCareConnections() {
  const { client, patientId } = useApiClient();

  return useQuery<CareConnection[]>({
    queryKey: queryKey(patientId),
    queryFn: async () => {
      const response = await client.GET("/patients/{patientId}/care-team", {
        params: { path: { patientId } },
      });
      if (response.data) return response.data.data;
      throwApiError(
        response.error,
        "We couldn’t load your care-team records.",
        "useCareConnections",
        response.response?.status,
      );
    },
    enabled: !!patientId,
  });
}

export function useCreateCareConnection() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CareTeamRecordInput) => {
      const response = await client.POST("/patients/{patientId}/care-team", {
        params: { path: { patientId } },
        body,
      });
      if (response.data) return response.data.data;
      throwApiError(
        response.error,
        "We couldn’t save this care-team record.",
        "useCreateCareConnection",
        response.response?.status,
      );
    },
    onSuccess: (connection: CareConnection) => {
      queryClient.setQueryData<CareConnection[]>(
        queryKey(patientId),
        (current) => [
          connection,
          ...(current ?? []).filter((item) => item.id !== connection.id),
        ],
      );
      queryClient.invalidateQueries({ queryKey: queryKey(patientId) });
    },
  });
}

export function useUpdateCareConnection() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      connectionId,
      body,
    }: {
      connectionId: string;
      body: CareTeamRecordUpdateInput;
    }) => {
      const response = await client.PATCH(
        "/patients/{patientId}/care-team/{recordId}",
        {
          params: { path: { patientId, recordId: connectionId } },
          body,
        },
      );
      if (response.data) return response.data.data;
      throwApiError(
        response.error,
        "We couldn’t update this care-team record.",
        "useUpdateCareConnection",
        response.response?.status,
      );
    },
    onSuccess: (connection: CareConnection) => {
      queryClient.setQueryData<CareConnection[]>(
        queryKey(patientId),
        (current) =>
          (current ?? []).map((item) =>
            item.id === connection.id ? connection : item,
          ),
      );
      queryClient.invalidateQueries({ queryKey: queryKey(patientId) });
    },
  });
}

export function useDeleteCareConnection() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (connectionId: string) => {
      const response = await client.DELETE(
        "/patients/{patientId}/care-team/{recordId}",
        { params: { path: { patientId, recordId: connectionId } } },
      );
      if (response.data) return response.data.data;
      throwApiError(
        response.error,
        "We couldn’t remove this care-team record.",
        "useDeleteCareConnection",
        response.response?.status,
      );
    },
    onSuccess: (_, connectionId) => {
      queryClient.setQueryData<CareConnection[]>(
        queryKey(patientId),
        (current) => (current ?? []).filter((item) => item.id !== connectionId),
      );
      queryClient.invalidateQueries({ queryKey: queryKey(patientId) });
    },
  });
}
